import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiCheckCircle, FiClock, FiSend, FiArrowRight } from "react-icons/fi";
import { transfersApi, publicUploadBase } from "../../services/transfersApi";
import { useTransferUpload } from "../../hooks/useTransferUpload";
import { SHARE_URL } from "../../utils/appHost";
import TransferDropzone from "../../components/share/TransferDropzone";
import UploadProgressList from "../../components/share/UploadProgressList";
import OverallProgress from "../../components/share/OverallProgress";
import { formatBytes } from "../../components/share/fileKinds";
import { OwnerHeader, PublicFooter, PublicNotFound } from "./PublicTransferPage";
import "./share.css";

/** Request link (/r/:token): a client uploads files to the photographer, who gets an email. */
export default function PublicRequestPage() {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | not_found
  const [phase, setPhase] = useState("compose"); // compose | uploading | done
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState({ count: 0, size: 0 });
  const upload = useTransferUpload();

  useEffect(() => {
    transfersApi
      .getPublic(token)
      .then((res) => {
        if (res.data.kind !== "request") throw new Error("not a request link");
        setData(res.data);
        setStatus("ready");
      })
      .catch(() => setStatus("not_found"));
  }, [token]);

  useEffect(() => {
    if (data?.subject) document.title = `${data.subject} · Pic-Loud Share`;
  }, [data]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setPhase("uploading");
    try {
      const extra = { uploader_name: name.trim() || null, uploader_email: email.trim() || null };
      const { fileIds, bytes } = await upload.start({ base: publicUploadBase(token), extra });
      if (fileIds.length === 0) {
        setError(t("share.upload.all_failed"));
        setPhase("compose");
        return;
      }
      await transfersApi.finalizePublic(token, fileIds);
      setSent({ count: fileIds.length, size: bytes });
      setPhase("done");
    } catch (err) {
      setError(err?.response?.data?.detail || t("share.errors.generic"));
      setPhase("compose");
    }
  };

  const sendMore = () => {
    upload.reset();
    setPhase("compose");
  };

  if (status === "loading") {
    return <div className="shr-root"><div className="shr-center"><div className="shr-spinner" /></div></div>;
  }
  if (status === "not_found") return <PublicNotFound />;

  return (
    <div className="shr-root">
      <main className="shr-main narrow">
        <div className="shr-card">
          <OwnerHeader owner={data.owner} subject={data.subject}>
            <p className="shr-muted mt-2">
              {t("share.request_page.intro", { name: data.owner.company_name || t("share.request_page.photographer") })}
            </p>
            {data.expires_at && (
              <div className="shr-public-meta">
                <span>
                  <FiClock />
                  {t("share.public.open_until", { date: new Date(data.expires_at).toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short" }) })}
                </span>
              </div>
            )}
          </OwnerHeader>

          {data.message && <p className="shr-public-message">{data.message}</p>}

          {phase === "done" ? (
            <>
              <div className="shr-callout">
                <FiCheckCircle size={18} />
                <span>{t("share.request_page.done", { count: sent.count, size: formatBytes(sent.size) })}</span>
              </div>
              <button type="button" className="shr-btn ghost block" onClick={sendMore}>
                {t("share.request_page.send_more")}
              </button>
              <div className="shr-callout info mt-4" style={{ alignItems: "center" }}>
                <FiSend size={18} />
                <span style={{ flex: 1 }}>{t("share.request_page.cta_text")}</span>
                <a className="shr-btn dark sm" href={`${SHARE_URL}/`}>
                  {t("share.request_page.cta_btn")} <FiArrowRight />
                </a>
              </div>
            </>
          ) : (
            <form onSubmit={submit}>
              {error && <div className="shr-callout error">{error}</div>}

              <div className="row g-2">
                <div className="col-sm-6 shr-field">
                  <label className="shr-label" htmlFor="shr-up-name">{t("share.request_page.your_name")}</label>
                  <input id="shr-up-name" className="shr-input" value={name} maxLength={255} onChange={(e) => setName(e.target.value)} disabled={phase === "uploading"} />
                </div>
                <div className="col-sm-6 shr-field">
                  <label className="shr-label" htmlFor="shr-up-email">{t("share.request_page.your_email")}</label>
                  <input id="shr-up-email" type="email" className="shr-input" value={email} onChange={(e) => setEmail(e.target.value)} disabled={phase === "uploading"} />
                </div>
              </div>

              {phase === "compose" && <TransferDropzone onFiles={upload.addFiles} />}
              {phase === "uploading" && (
                <OverallProgress uploadedBytes={upload.uploadedBytes} totalBytes={upload.totalBytes} speed={upload.speed} eta={upload.eta} />
              )}
              <UploadProgressList items={upload.items} onRemove={phase === "compose" ? upload.removeFile : undefined} />

              <button type="submit" className="shr-btn primary block mt-3" disabled={phase !== "compose" || upload.files.length === 0}>
                <FiSend />
                {phase === "uploading" ? t("share.request_page.sending") : t("share.request_page.send_cta", { count: upload.files.length })}
              </button>
              {phase === "uploading" && (
                <button type="button" className="shr-btn ghost block mt-2" onClick={upload.cancel}>
                  {t("share.upload.cancel")}
                </button>
              )}
            </form>
          )}
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
